<!-- Copyright (C) 2024 by Posit Software, PBC. -->

<template>
  <component
    :is="to ? RouterLink : 'button'"
    ref="button"
    :to="to"
    v-bind="$attrs"
    :aria-label="ariaLabel || label"
    :class="['rs-button', noLabel, type, labelWidth]"
    :type="buttonType"
    @click="emit('click', $event)"
  >
    <div class="wrapper" :class="[alignment, size]">
      <svg
        v-if="spinner"
        :class="{
          'w-4': size === ButtonSize.Small || size === ButtonSize.Normal,
          'w-8': size === ButtonSize.Large,
          'me-1':
            !iconOnly &&
            size === ButtonSize.Small &&
            alignment === ButtonAlignment.Horizontal,
          'me-2':
            !iconOnly &&
            size === ButtonSize.Normal &&
            alignment === ButtonAlignment.Horizontal,
          'me-3':
            !iconOnly &&
            size === ButtonSize.Large &&
            alignment === ButtonAlignment.Horizontal,
          'mb-1':
            !iconOnly &&
            size === ButtonSize.Small &&
            alignment === ButtonAlignment.Vertical,
          'mb-2':
            !iconOnly &&
            size === ButtonSize.Normal &&
            alignment === ButtonAlignment.Vertical,
          'mb-3':
            !iconOnly &&
            size === ButtonSize.Large &&
            alignment === ButtonAlignment.Vertical,
        }"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g class="spinner-container">
          <circle cx="12" cy="12" r="9.5" fill="none" stroke-width="3" />
        </g>
      </svg>
      <img v-else-if="icon" alt="" :src="icon" />
      <span v-if="!hasDefaultSlot && !iconOnly">{{ label }}</span>
      <!-- @slot The text label of the button (can also be set as the label prop) -->
      <slot />
    </div>
  </component>
</template>

<script lang="ts">
export const ButtonType = {
  Primary: "primary",
  Secondary: "secondary",
  Link: "link",
} as const;

export const ButtonAlignment = {
  Horizontal: "horizontal",
  Vertical: "vertical",
} as const;

export const ButtonSize = {
  Small: "small",
  Normal: "normal",
  Large: "large",
} as const;
</script>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";

interface Props {
  /**
   * The text label of the button (can also be set as the default slot)
   */
  label: string;
  /**
   * The type of button
   * @values primary, secondary, link
   * @type string
   */
  type?: (typeof ButtonType)[keyof typeof ButtonType];
  /**
   * The size of the button
   * @values small, normal, large
   * @type string
   */
  size?: (typeof ButtonSize)[keyof typeof ButtonSize];
  /**
   * The icon to display on the button
   *
   * ```
   * import iconNew from '@/assets/images/iconNew.svg'
   * import RSButton from '@/components/RSButton'
   *
   * const ButtonPanel = ({
   *   components: { RSButton }
   *   data: () => ({ iconNew }),
   *   template: `
   *     <div class="button-panel">
   *       <RSButton label="New" icon="iconNew" @click="onClick" />
   *     </div>
   *   `
   * })
   * ```
   */
  icon?: string;
  /**
   * A spinner will be shown. If an icon is passed the spinner will be shown
   * instead of the icon.
   * @type boolean
   */
  spinner?: boolean;
  /**
   * The alignment of the button label with the icon.
   * Unnecessary if the button has no icon or no text.
   * @values horizontal, vertical
   * @type string
   */
  alignment?: (typeof ButtonAlignment)[keyof typeof ButtonAlignment];
  /**
   * Show the icon and only render the label as an aria-label.
   * @type boolean
   */
  iconOnly?: boolean;
  /**
   * Set the button width from the label, rather than a uniform
   * minimum width.
   * @type boolean
   */
  useLabelWidth?: boolean;
  ariaLabel?: string;
  to?: RouteLocationRaw;
}

const props = withDefaults(defineProps<Props>(), {
  type: ButtonType.Primary,
  size: ButtonSize.Normal,
  icon: undefined,
  spinner: false,
  alignment: ButtonAlignment.Horizontal,
  iconOnly: false,
  useLabelWidth: false,
  ariaLabel: undefined,
  to: undefined,
});

const emit = defineEmits<(e: "click", event: MouseEvent) => void>();

const slots = defineSlots<{
  default(): unknown;
}>();

defineOptions({
  inheritAttrs: false,
});

const button = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>("button");

const buttonType = computed(() => {
  return props.type === ButtonType.Primary ? "submit" : "button";
});

const hasDefaultSlot = computed(() => {
  return Boolean(slots.default);
});

const noLabel = computed(() => {
  return props.iconOnly ? "no-label" : "";
});

const labelWidth = computed(() => {
  return props.useLabelWidth ? "label-width" : "";
});

function focusElement() {
  button.value.focus();
}

defineExpose({ focusElement });
</script>

<style scoped lang="scss">
@use "Styles/shared/_colors";
@use "Styles/shared/_variables";
@use "Styles/shared/_mixins";

// Spinner animation styles
.spinner-container {
  transform-origin: center;
  animation: spinner-rotate 2s linear infinite;

  circle {
    stroke-linecap: round;
    animation: spinner-dash 1.5s ease-in-out infinite;
  }
}

@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes spinner-dash {
  0% {
    stroke-dasharray: 0 150;
    stroke-dashoffset: 0;
  }
  47.5% {
    stroke-dasharray: 42 150;
    stroke-dashoffset: -16;
  }
  95%,
  100% {
    stroke-dasharray: 42 150;
    stroke-dashoffset: -59;
  }
}

.rs-button,
a.rs-button {
  min-width: 8.75rem;
  margin: 0;
  padding: 0.7rem 1.1rem;
  font-size: variables.$rs-font-size-normal;
  line-height: 1rem;
  overflow: visible;
  border: none;
  border-radius: 3px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition-duration: 250ms;
  text-align: center;

  &[disabled] {
    cursor: not-allowed;
  }

  &.label-width {
    min-width: unset;
  }

  &.no-label {
    min-width: unset;

    .wrapper.vertical.vertical,
    .wrapper.horizontal.horizontal {
      img {
        margin-right: 0;
        margin-bottom: 0;
      }
    }
  }

  @include mixins.control-visible-focus;

  &.primary {
    background-color: colors.$color-primary;
    color: colors.$color-primary-inverse;

    &:hover:not([disabled]) {
      background-color: colors.$color-primary-dark;
    }
  }

  &.secondary {
    background-color: colors.$color-secondary;
    color: colors.$color-secondary-inverse;

    &:hover:not([disabled]) {
      background-color: colors.$color-secondary-hover;
    }
  }

  &.link {
    background-color: transparent;
    border: none;
    box-shadow: none;
    color: colors.$color-primary-dark;
    min-width: unset;
    padding-left: 0.5rem;
    padding-right: 0.5rem;

    &:hover:not([disabled]) {
      background-color: colors.$color-light-blue;
      color: colors.$color-blue;
    }
  }

  &.full-width {
    width: 100%;
    max-width: 100%;
  }

  .wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    &.vertical {
      flex-direction: column;

      img {
        margin-right: 0;
        margin-bottom: 0.5rem;
      }

      &.small {
        img {
          margin-right: 0;
          margin-bottom: 0.3rem;
        }
      }

      &.large {
        img {
          margin-right: 0;
          margin-bottom: 0.7rem;
        }
      }
    }

    img {
      width: 24px;
      margin-right: 0.5rem;
    }

    &.small {
      font-size: variables.$rs-font-size-small;

      img {
        width: 16px;
        margin-right: 0.3rem;
      }
    }

    &.large {
      font-size: variables.$rs-font-size-large;

      img {
        width: 40px;
        margin-right: 0.7rem;
      }
    }
  }
}

a.rs-button {
  display: inline-block;
  font-weight: normal;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}
</style>
